import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnChanges, OnDestroy, Output, PLATFORM_ID, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import JSZip, { JSZipObject } from 'jszip';
import { Subscription } from 'rxjs';
import { UiPopoverService } from 'src/app/components/elements/ui-popover/ui-popover.service';
import { UiTooltipService } from 'src/app/components/elements/ui-tooltip/ui-tooltip.service';
import { ThemeService } from 'src/app/services/theme.service';

type ZipPreviewMode = 'preview' | 'code';
type ZipFileKind = 'code' | 'markdown' | 'csv' | 'html' | 'svg' | 'image' | 'audio' | 'video' | 'binary';
type ZipNodeType = 'folder' | 'file';
type ZipExplorerActionType = 'create-file' | 'create-folder' | 'rename';
type ZipExplorerVisibility = 'expanded' | 'hidden';

export interface ZipPreviewViewerState {
  canTogglePreviewMode: boolean;
  canSaveArchive: boolean;
  archiveSaving: boolean;
  previewMode: ZipPreviewMode | null;
  explorerVisibility: ZipExplorerVisibility;
}

interface ZipExplorerActionState {
  type: ZipExplorerActionType;
  value: string;
  error: string | null;
}

interface ZipTreeNode {
  name: string;
  path: string;
  type: 'folder' | 'file';
  depth: number;
  expanded: boolean;
  children: ZipTreeNode[];
}

interface SelectedZipFile {
  path: string;
  name: string;
  kind: ZipFileKind;
  language: string;
  mimeType: string;
  sizeBytes: number;
  originalText?: string;
  rawText?: string;
  previewText?: string;
  previewUrl?: string;
  truncated: boolean;
}

interface ArchiveFileDescriptor {
  path: string;
  entry: JSZipObject;
}

const MAX_TEXT_PREVIEW_BYTES = 1024 * 1024;
const FILE_PICK_PRIORITY = [
  'index.html',
  'dist/index.html',
  'public/index.html',
  'build/index.html'
];

@Component({
  selector: 'app-artifact-zip-preview',
  templateUrl: './artifact-zip-preview.component.html',
  styleUrl: './artifact-zip-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.artifact-zip-preview--detached]': 'detachedPreview'
  }
})
export class ArtifactZipPreviewComponent implements OnChanges, OnDestroy {
  @Input() archiveUrl: string | null = null;
  @Input() archiveStorageUri: string | null = null;
  @Input() archiveTitle = 'ZIP archive';
  @Input() detachedPreview = false;
  @Input() parseMarkdown: (value: string) => string = (value: string) => value;
  @Output() archiveSaved = new EventEmitter<{ downloadUrl: string; sizeBytes: number }>();
  @Output() archiveDeleted = new EventEmitter<{ storageTarget: string }>();
  @Output() viewerStateChange = new EventEmitter<ZipPreviewViewerState>();
  @ViewChild('addMenuPopoverTemplate') addMenuPopoverTemplate!: TemplateRef<unknown>;
  @ViewChild('archiveDeletePopoverTemplate') archiveDeletePopoverTemplate!: TemplateRef<unknown>;
  @ViewChild('renamePopoverTemplate') renamePopoverTemplate!: TemplateRef<unknown>;
  @ViewChild('deletePopoverTemplate') deletePopoverTemplate!: TemplateRef<unknown>;
  @ViewChild('renamePopoverInput') renamePopoverInput?: ElementRef<HTMLInputElement>;

  loading = false;
  error: string | null = null;
  fileCount = 0;
  treeNodes: ZipTreeNode[] = [];
  selectedFile: SelectedZipFile | null = null;
  selectedFolderPath: string | null = null;
  selectedFileLoading = false;
  selectedFileError: string | null = null;
  previewMode: ZipPreviewMode = 'preview';
  explorerVisibility: ZipExplorerVisibility = 'expanded';
  archiveSaving = false;
  archiveDeleting = false;
  archiveSaveError: string | null = null;
  archiveSaveStatus: string | null = null;
  activeNodePath: string | null = null;
  activeNodeType: ZipNodeType | null = null;
  activePopoverKind: 'add' | 'archive-delete' | 'rename' | 'delete' | null = null;
  renameTargetNode: ZipTreeNode | null = null;
  deleteTargetNode: ZipTreeNode | null = null;
  renameDraft = '';
  renameError: string | null = null;
  draggedNodePath: string | null = null;
  draggedNodeType: ZipNodeType | null = null;
  dropDirectoryPath: string | null = null;
  dropHoverNodePath: string | null = null;
  explorerAction: ZipExplorerActionState | null = null;
  editorOptions = this.buildEditorOptions('plaintext', true);
  explorerWidth = 320;
  isExplorerResizing = false;

  private archiveZip: JSZip | null = null;
  private archiveEntries = new Map<string, ArchiveFileDescriptor>();
  private readonly explicitFolderPaths = new Set<string>();
  private readonly modifiedFiles = new Map<string, string>();
  private readonly createdObjectUrls = new Set<string>();
  private readonly objectUrlCache = new Map<string, string>();
  private readonly subscriptions = new Subscription();
  private loadVersion = 0;
  private archiveStructureDirty = false;
  private savedArchiveStructureSignature = '';
  private loadedArchiveSourceKey: string | null = null;
  private pendingArchiveSourceKey: string | null = null;
  private loadedArchiveUrl: string | null = null;
  private explorerResizeStartX = 0;
  private explorerResizeStartWidth = 320;
  private explorerResizePointerId: number | null = null;
  private explorerResizeHandle: HTMLElement | null = null;
  private readonly minExplorerWidth = 68;
  private readonly maxExplorerWidth = 420;
  private readonly railExplorerThreshold = 112;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    public theme: ThemeService,
    private afStorage: AngularFireStorage,
    private popoverService: UiPopoverService,
    private tooltipService: UiTooltipService,
    private cdr: ChangeDetectorRef
  ) {
    this.subscriptions.add(this.theme.mode$.subscribe(mode => {
      this.editorOptions = {
        ...this.editorOptions,
        theme: mode === 'light' ? 'vs' : 'vs-dark'
      };
      this.cdr.markForCheck();
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['archiveUrl'] && !changes['archiveStorageUri']) {
      return;
    }

    const nextArchiveSourceKey = this.getArchiveSourceKey();
    const nextArchiveUrl = String(this.archiveUrl || '').trim() || null;
    if (nextArchiveUrl && nextArchiveUrl === this.loadedArchiveUrl && !!this.archiveZip) {
      return;
    }

    if (nextArchiveSourceKey && (nextArchiveSourceKey === this.pendingArchiveSourceKey || (nextArchiveSourceKey === this.loadedArchiveSourceKey && !!this.archiveZip))) {
      return;
    }

    void this.loadArchive(nextArchiveSourceKey);
  }

  ngOnDestroy(): void {
    this.stopExplorerResize();
    this.tooltipService.hideImmediate();
    if (this.activePopoverKind) {
      this.popoverService.close();
    }
    this.subscriptions.unsubscribe();
    this.resetArchiveState();
  }

  @HostListener('document:pointermove', ['$event'])
  onDocumentPointerMove(event: PointerEvent): void {
    if (!this.isExplorerResizing) {
      return;
    }

    const deltaX = this.explorerResizeStartX - event.clientX;
    const nextWidth = this.getClampedExplorerWidth(this.explorerResizeStartWidth + deltaX);

    if (nextWidth === this.explorerWidth) {
      return;
    }

    this.explorerWidth = nextWidth;
    this.cdr.markForCheck();
  }

  @HostListener('document:pointerup', ['$event'])
  onDocumentPointerUp(_event: PointerEvent): void {
    this.stopExplorerResize();
  }

  @HostListener('document:pointercancel', ['$event'])
  onDocumentPointerCancel(_event: PointerEvent): void {
    this.stopExplorerResize();
  }

  @HostListener('window:blur')
  onWindowBlur(): void {
    this.stopExplorerResize();
  }

  async togglePreviewMode(mode: ZipPreviewMode): Promise<void> {
    if (!this.selectedFile || this.previewMode === mode) {
      return;
    }

    if (mode === 'preview') {
      await this.refreshSelectedFilePreview(this.selectedFile);
    }

    this.previewMode = mode;
    this.emitViewerStateChange();
    this.cdr.markForCheck();
  }

  async onTreeNodeClick(node: ZipTreeNode): Promise<void> {
    this.tooltipService.hideImmediate();
    this.closeAnyPopover();

    if (node.type === 'folder') {
      this.activeNodePath = node.path;
      this.activeNodeType = 'folder';
      this.selectedFolderPath = node.path;
      this.selectedFile = null;
      this.selectedFileLoading = false;
      this.selectedFileError = null;
      this.previewMode = 'preview';
      node.expanded = !node.expanded;
      this.emitViewerStateChange();
      this.cdr.markForCheck();
      return;
    }

    this.activeNodePath = node.path;
    this.activeNodeType = 'file';
    this.selectedFolderPath = null;
    await this.selectFile(node.path);
  }

  trackByTreeNode(_index: number, node: ZipTreeNode): string {
    return node.path;
  }

  isActiveNode(node: ZipTreeNode): boolean {
    return this.activeNodePath === node.path && this.activeNodeType === node.type;
  }

  hasActiveNode(): boolean {
    return !!this.activeNodePath && !!this.activeNodeType;
  }

  isDraggingNode(): boolean {
    return !!this.draggedNodePath;
  }

  canTogglePreviewMode(file: SelectedZipFile | null): boolean {
    return !!file && (file.kind === 'html' || file.kind === 'svg' || file.kind === 'markdown' || file.kind === 'csv');
  }

  canShowDetachedPreviewModeToggle(): boolean {
    return this.canTogglePreviewMode(this.selectedFile);
  }

  getSelectedPreviewMode(): ZipPreviewMode {
    return this.previewMode;
  }

  getResolvedExplorerWidth(): number {
    return this.explorerWidth;
  }

  isExplorerRail(): boolean {
    return !this.isExplorerHidden() && this.explorerWidth <= this.railExplorerThreshold;
  }

  isExplorerHidden(): boolean {
    return this.explorerVisibility === 'hidden';
  }

  getTreeNodeIndent(node: ZipTreeNode): number {
    return this.isExplorerRail() ? 0 : 12 + (node.depth * 16);
  }

  setExplorerVisibility(mode: ZipExplorerVisibility): void {
    if (this.explorerVisibility === mode) {
      return;
    }

    if (mode !== 'expanded') {
      this.stopExplorerResize();
    }

    this.closeAnyPopover();
    this.explorerVisibility = mode;
    this.emitViewerStateChange();
    this.cdr.markForCheck();
  }

  onExplorerDividerPointerDown(event: PointerEvent): void {
    if (this.isExplorerHidden() || event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget as HTMLElement | null;
    handle?.setPointerCapture?.(event.pointerId);
    this.isExplorerResizing = true;
    this.explorerResizeStartX = event.clientX;
    this.explorerResizeStartWidth = this.explorerWidth;
    this.explorerResizePointerId = event.pointerId;
    this.explorerResizeHandle = handle;
    this.setResizeCursorState(true);
    this.cdr.markForCheck();
  }

  onExplorerEdgeButtonPointerDown(event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onExplorerDividerKeydown(event: KeyboardEvent): void {
    if (this.isExplorerHidden()) {
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.explorerWidth = this.getClampedExplorerWidth(this.explorerWidth + 24);
      this.cdr.markForCheck();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.explorerWidth = this.getClampedExplorerWidth(this.explorerWidth - 24);
      this.cdr.markForCheck();
      return;
    }
  }

  hideExplorer(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.setExplorerVisibility('hidden');
  }

  showExplorer(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.setExplorerVisibility('expanded');
  }

  onTreeNodeTooltipEnter(node: ZipTreeNode, event: MouseEvent | FocusEvent): void {
    if (!this.isExplorerRail()) {
      return;
    }

    const element = event.currentTarget as HTMLElement | null;
    if (!element) {
      return;
    }

    this.tooltipService.show({
      element,
      content: node.path,
      placement: 'left-center',
      offset: 12,
      trigger: 'manual',
      delay: 120,
      dismissDelay: 40,
      suppressOnMobile: true
    });
  }

  onTreeNodeTooltipLeave(): void {
    this.tooltipService.hide();
  }

  getViewerMode(file: SelectedZipFile | null): 'html' | 'markdown' | 'csv' | 'image' | 'audio' | 'video' | 'code' | 'binary' {
    if (!file) {
      return 'binary';
    }

    if ((file.kind === 'html' || file.kind === 'svg') && this.previewMode === 'preview') {
      return 'html';
    }

    if (file.kind === 'markdown' && this.previewMode === 'preview') {
      return 'markdown';
    }

    if (file.kind === 'csv' && this.previewMode === 'preview') {
      return 'csv';
    }

    if (file.kind === 'image') {
      return 'image';
    }

    if (file.kind === 'audio') {
      return 'audio';
    }

    if (file.kind === 'video') {
      return 'video';
    }

    if (file.kind === 'binary') {
      return 'binary';
    }

    return 'code';
  }

  getSelectedFileContents(): string {
    return this.selectedFile?.rawText || '';
  }

  getCsvRows(file: SelectedZipFile | null): string[][] {
    if (!file || file.kind !== 'csv') {
      return [];
    }

    return this.parseCsvRows(file.rawText || '');
  }

  getCsvHeader(file: SelectedZipFile | null): string[] {
    return this.getCsvRows(file)[0] || [];
  }

  getCsvBody(file: SelectedZipFile | null): string[][] {
    return this.getCsvRows(file).slice(1);
  }

  canEditFile(file: SelectedZipFile | null): boolean {
    return !!file && this.isTextFileKind(file.kind);
  }

  hasPendingArchiveChanges(): boolean {
    return this.modifiedFiles.size > 0 || this.captureArchiveStructureSignature() !== this.savedArchiveStructureSignature;
  }

  canSaveArchive(): boolean {
    return this.hasPendingArchiveChanges() && !this.archiveSaving;
  }

  canDeleteArchive(): boolean {
    return !!this.getStorageReferenceTarget() && !this.archiveDeleting;
  }

  hasUnsavedChanges(): boolean {
    return this.hasPendingArchiveChanges();
  }

  getArchiveStatusClass(): string | null {
    if (this.archiveSaveError) {
      return 'zip-preview-sidebar__status--error';
    }

    if (this.hasUnsavedChanges()) {
      return 'zip-preview-sidebar__status--dirty';
    }

    return null;
  }

  getArchiveStatusLabel(): string {
    if (this.archiveDeleting) {
      return 'Deleting archive...';
    }

    if (this.archiveSaving) {
      return 'Saving archive...';
    }

    if (this.archiveSaveError) {
      return this.archiveSaveError;
    }

    if (this.archiveSaveStatus) {
      return this.archiveSaveStatus;
    }

    return this.hasPendingArchiveChanges()
      ? 'Unsaved file changes inside this archive.'
      : 'Create, move, rename, or edit files, then save to re-pack the archive.';
  }

  openAddMenu(event: MouseEvent): void {
    event?.stopPropagation();
    const anchor = event.currentTarget as HTMLElement | null;
    if (!anchor || !this.addMenuPopoverTemplate) {
      return;
    }

    this.resetRenamePopoverState();
    this.activePopoverKind = 'add';
    this.popoverService.open({
      element: anchor,
      placement: 'bottom-right',
      align: 'end',
      bodyTemplate: this.addMenuPopoverTemplate,
      backdrop: true,
      noPadding: true,
      dimensions: { width: 180 },
      displayMode: this.theme.isLight ? 'light' : 'dark',
      onClose: () => {
        this.resetAddPopoverState();
        this.cdr.markForCheck();
      }
    });
  }

  openArchiveDeletePopover(event: MouseEvent): void {
    event?.stopPropagation();
    const anchor = event.currentTarget as HTMLElement | null;
    if (!anchor || !this.archiveDeletePopoverTemplate || !this.canDeleteArchive()) {
      return;
    }

    this.resetAddPopoverState();
    this.resetRenamePopoverState();
    this.resetDeletePopoverState();
    this.activePopoverKind = 'archive-delete';
    this.popoverService.open({
      element: anchor,
      placement: 'bottom-right',
      align: 'end',
      bodyTemplate: this.archiveDeletePopoverTemplate,
      backdrop: true,
      noPadding: true,
      dimensions: { width: 260 },
      displayMode: this.theme.isLight ? 'light' : 'dark',
      onClose: () => {
        this.resetArchiveDeletePopoverState();
        this.cdr.markForCheck();
      }
    });
  }

  closeArchiveDeletePopover(): void {
    if (this.activePopoverKind === 'archive-delete') {
      this.popoverService.close();
      return;
    }

    this.resetArchiveDeletePopoverState();
    this.cdr.markForCheck();
  }

  async confirmDeleteArchive(event?: Event): Promise<void> {
    event?.stopPropagation();

    const storageTarget = this.getStorageReferenceTarget();
    if (!storageTarget || this.archiveDeleting) {
      return;
    }

    this.archiveDeleting = true;
    this.archiveSaveError = null;
    this.archiveSaveStatus = null;
    this.closeAnyPopover();
    this.emitViewerStateChange();
    this.cdr.markForCheck();

    try {
      const storageRef = this.afStorage.storage.refFromURL(storageTarget);
      await storageRef.delete();
      this.archiveDeleted.emit({ storageTarget });
      this.resetArchiveState();
    } catch (error: any) {
      if (error?.code === 'storage/object-not-found') {
        this.archiveDeleted.emit({ storageTarget });
        this.resetArchiveState();
        return;
      }

      this.archiveSaveError = error?.message || 'Failed to delete archive from storage.';
      this.archiveSaveStatus = null;
      this.archiveDeleting = false;
      this.emitViewerStateChange();
      this.cdr.markForCheck();
    }
  }

  async createRootFile(event?: Event): Promise<void> {
    event?.stopPropagation();
    this.closeAnyPopover();
    await this.createArchiveFile(this.buildUniquePath('', 'untitled.txt', 'file'));
  }

  createRootFolder(event?: Event): void {
    event?.stopPropagation();
    this.closeAnyPopover();
    this.createArchiveFolder(this.buildUniquePath('', 'new-folder', 'folder'));
  }

  openRenamePopover(node: ZipTreeNode, event?: MouseEvent): void {
    event?.stopPropagation();
    const anchor = event?.currentTarget as HTMLElement | null;
    if (!anchor || !this.renamePopoverTemplate) {
      return;
    }

    this.resetAddPopoverState();
    this.resetDeletePopoverState();
    this.activeNodePath = node.path;
    this.activeNodeType = node.type;
    this.renameTargetNode = node;
    this.activePopoverKind = 'rename';
    this.renameDraft = this.getPathBasename(node.path);
    this.renameError = null;
    this.popoverService.open({
      element: anchor,
      placement: 'right-top',
      align: 'start',
      bodyTemplate: this.renamePopoverTemplate,
      backdrop: true,
      noPadding: true,
      positionDelay: 10,
      dimensions: { width: 260 },
      displayMode: this.theme.isLight ? 'light' : 'dark',
      onClose: () => {
        this.resetRenamePopoverState();
        this.cdr.markForCheck();
      }
    });

    this.cdr.detectChanges();
    this.focusRenamePopoverInput();
  }

  openDeletePopover(node: ZipTreeNode, event?: MouseEvent): void {
    event?.stopPropagation();
    const anchor = event?.currentTarget as HTMLElement | null;
    if (!anchor || !this.deletePopoverTemplate) {
      return;
    }

    this.resetAddPopoverState();
    this.resetRenamePopoverState();
    this.activeNodePath = node.path;
    this.activeNodeType = node.type;
    this.deleteTargetNode = node;
    this.activePopoverKind = 'delete';
    this.popoverService.open({
      element: anchor,
      placement: 'left-center',
      align: 'center',
      bodyTemplate: this.deletePopoverTemplate,
      backdrop: true,
      noPadding: true,
      dimensions: { width: 240 },
      displayMode: this.theme.isLight ? 'light' : 'dark',
      onClose: () => {
        this.resetDeletePopoverState();
        this.cdr.markForCheck();
      }
    });
  }

  closeRenamePopover(): void {
    if (this.activePopoverKind === 'rename') {
      this.popoverService.close();
      return;
    }

    this.resetRenamePopoverState();
    this.cdr.markForCheck();
  }

  closeDeletePopover(): void {
    if (this.activePopoverKind === 'delete') {
      this.popoverService.close();
      return;
    }

    this.resetDeletePopoverState();
    this.cdr.markForCheck();
  }

  updateRenameDraft(value: string): void {
    this.renameDraft = value;
    this.renameError = null;
    this.cdr.markForCheck();
  }

  async submitRename(node: ZipTreeNode | null = this.renameTargetNode, event?: Event): Promise<void> {
    event?.stopPropagation();

    if (!node) {
      return;
    }

    const nextName = String(this.renameDraft || '').trim();
    if (!nextName) {
      this.renameError = 'Enter a name first.';
      this.cdr.markForCheck();
      return;
    }

    if (nextName.includes('/') || nextName.includes('\\')) {
      this.renameError = 'Names cannot contain path separators.';
      this.cdr.markForCheck();
      return;
    }

    try {
      this.activeNodePath = node.path;
      this.activeNodeType = node.type;
      const parentDirectory = this.getParentDirectory(node.path);
      const nextPath = this.normalizeArchivePath(parentDirectory ? `${parentDirectory}/${nextName}` : nextName);
      await this.renameOrMoveNode(nextPath);
      this.popoverService.close();
      this.cdr.markForCheck();
    } catch (error) {
      this.renameError = (error as Error).message || 'Unable to rename this item.';
      this.cdr.markForCheck();
    }
  }

  async confirmDeleteNode(node: ZipTreeNode | null = this.deleteTargetNode, event?: Event): Promise<void> {
    event?.stopPropagation();

    if (!node) {
      return;
    }

    await this.deleteArchiveNode(node.path, node.type);
    this.popoverService.close();
  }

  onTreeDragStart(node: ZipTreeNode, event: DragEvent): void {
    this.tooltipService.hideImmediate();
    this.draggedNodePath = node.path;
    this.draggedNodeType = node.type;

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', node.path);
    }

    this.cdr.markForCheck();
  }

  onTreeDragEnd(): void {
    this.draggedNodePath = null;
    this.draggedNodeType = null;
    this.dropDirectoryPath = null;
    this.dropHoverNodePath = null;
    this.cdr.markForCheck();
  }

  onNodeDragOver(node: ZipTreeNode, event: DragEvent): void {
    const targetDirectory = this.getDropDirectoryForNode(node);
    if (!this.canHoverDirectoryTarget(targetDirectory)) {
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    this.dropDirectoryPath = targetDirectory;
    this.dropHoverNodePath = node.path;
    this.cdr.markForCheck();
  }

  onNodeDragLeave(node: ZipTreeNode, event: DragEvent): void {
    const nextTarget = event.relatedTarget;
    if (event.currentTarget instanceof HTMLElement && nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
      return;
    }

    this.clearDropDirectory(this.getDropDirectoryForNode(node), node.path);
  }

  async onNodeDrop(node: ZipTreeNode, event: DragEvent): Promise<void> {
    event.preventDefault();
    this.dropHoverNodePath = node.path;
    await this.moveDraggedNodeToDirectory(this.getDropDirectoryForNode(node));
  }

  onRootDragOver(event: DragEvent): void {
    if (!this.canDropIntoDirectory('')) {
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    this.dropDirectoryPath = '';
    this.dropHoverNodePath = '';
    this.cdr.markForCheck();
  }

  onRootDragLeave(): void {
    this.clearDropDirectory('', '');
  }

  async onRootDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    await this.moveDraggedNodeToDirectory('');
  }

  async duplicateRenameTarget(event?: Event): Promise<void> {
    event?.stopPropagation();

    if (!this.renameTargetNode || this.renameTargetNode.type !== 'file') {
      return;
    }

    try {
      await this.duplicateArchiveFile(this.renameTargetNode.path);
      this.popoverService.close();
      this.cdr.markForCheck();
    } catch (error) {
      this.renameError = (error as Error).message || 'Unable to duplicate this file.';
      this.cdr.markForCheck();
    }
  }

  isDropDirectoryActive(pathValue: string): boolean {
    return this.dropDirectoryPath === pathValue;
  }

  isDropHoverNode(node: ZipTreeNode): boolean {
    return this.dropHoverNodePath === node.path;
  }

  getDropHintLabelForNode(node: ZipTreeNode): string {
    const targetDirectory = this.getDropDirectoryForNode(node);
    const unchanged = this.isDropLocationUnchanged(targetDirectory);
    if (!targetDirectory) {
      return unchanged ? 'Already in archive root' : 'Drop into archive root';
    }

    const folderName = this.getPathBasename(targetDirectory);
    return unchanged ? `Already in ${folderName}` : `Drop into ${folderName}`;
  }

  getRootDropHintLabel(): string {
    return this.isDropLocationUnchanged('')
      ? 'Already in archive root'
      : 'Drop into archive root';
  }

  clearDropDirectory(pathValue: string, hoverNodePath: string | null = null): void {
    if (this.dropDirectoryPath === pathValue) {
      this.dropDirectoryPath = null;
      if (hoverNodePath === null || this.dropHoverNodePath === hoverNodePath) {
        this.dropHoverNodePath = null;
      }
      this.cdr.markForCheck();
    }
  }

  getExplorerActionTitle(): string {
    if (!this.explorerAction) {
      return '';
    }

    if (this.explorerAction.type === 'create-file') {
      return 'New file path';
    }

    if (this.explorerAction.type === 'create-folder') {
      return 'New folder path';
    }

    return this.activeNodeType === 'folder' ? 'Rename or move folder' : 'Rename or move file';
  }

  getExplorerActionHint(): string {
    if (!this.explorerAction) {
      return '';
    }

    if (this.explorerAction.type === 'rename') {
      return 'Change the full archive path to rename or move the current selection.';
    }

    return 'Use a relative path inside the archive. Parent folders are created automatically when needed.';
  }

  getExplorerActionSubmitLabel(): string {
    if (!this.explorerAction) {
      return 'Apply';
    }

    if (this.explorerAction.type === 'create-file') {
      return 'Create file';
    }

    if (this.explorerAction.type === 'create-folder') {
      return 'Create folder';
    }

    return 'Apply path';
  }

  getSelectedFolderName(): string {
    return this.getPathBasename(this.selectedFolderPath || '') || 'Folder';
  }

  getPathBasename(pathValue: string): string {
    const normalized = this.normalizeArchivePath(pathValue);
    if (!normalized) {
      return '';
    }

    const segments = normalized.split('/').filter(Boolean);
    return segments[segments.length - 1] || normalized;
  }

  startCreateFile(): void {
    this.openExplorerAction('create-file', this.buildSuggestedPath('create-file'));
  }

  startCreateFolder(): void {
    this.openExplorerAction('create-folder', this.buildSuggestedPath('create-folder'));
  }

  startRenameMove(): void {
    if (!this.activeNodePath || !this.activeNodeType) {
      return;
    }

    this.openExplorerAction('rename', this.activeNodePath);
  }

  cancelExplorerAction(): void {
    this.explorerAction = null;
    this.cdr.markForCheck();
  }

  updateExplorerActionValue(value: string): void {
    if (!this.explorerAction) {
      return;
    }

    this.explorerAction = {
      ...this.explorerAction,
      value,
      error: null
    };
    this.cdr.markForCheck();
  }

  async submitExplorerAction(): Promise<void> {
    const action = this.explorerAction;
    if (!action) {
      return;
    }

    try {
      if (action.type === 'create-file') {
        await this.createArchiveFile(action.value);
      } else if (action.type === 'create-folder') {
        this.createArchiveFolder(action.value);
      } else {
        await this.renameOrMoveNode(action.value);
      }

      this.explorerAction = null;
      this.cdr.markForCheck();
    } catch (error) {
      this.explorerAction = {
        ...action,
        error: (error as Error).message || 'Unable to apply this archive change.'
      };
      this.cdr.markForCheck();
    }
  }

  onEditorChange(value: string): void {
    if (!this.selectedFile || !this.canEditFile(this.selectedFile)) {
      return;
    }

    this.invalidateObjectUrlCacheForPath(this.selectedFile.path);
    this.selectedFile.rawText = value;
    this.selectedFile.sizeBytes = this.getTextByteSize(value);
    this.selectedFile.truncated = false;
    this.archiveSaveError = null;
    this.archiveSaveStatus = null;

    if (value === (this.selectedFile.originalText || '')) {
      this.modifiedFiles.delete(this.selectedFile.path);
    } else {
      this.modifiedFiles.set(this.selectedFile.path, value);
    }

    this.emitViewerStateChange();

    if (this.previewMode === 'preview' && (this.selectedFile.kind === 'html' || this.selectedFile.kind === 'svg')) {
      void this.refreshSelectedFilePreview(this.selectedFile);
      return;
    }

    this.cdr.markForCheck();
  }

  async saveArchive(): Promise<void> {
    if (!this.archiveZip || !this.hasPendingArchiveChanges() || this.archiveSaving) {
      return;
    }

    const storageTarget = this.getStorageReferenceTarget();
    if (!storageTarget) {
      this.archiveSaveError = 'This archive cannot be saved because no storage target is available.';
      this.archiveSaveStatus = null;
      this.emitViewerStateChange();
      this.cdr.markForCheck();
      return;
    }

    this.archiveSaving = true;
    this.archiveSaveError = null;
    this.archiveSaveStatus = null;
    this.emitViewerStateChange();
    this.cdr.markForCheck();

    try {
      for (const [path, value] of this.modifiedFiles.entries()) {
        this.archiveZip.file(path, value);
        const entry = this.archiveZip.file(path);
        if (entry) {
          this.archiveEntries.set(path, { path, entry });
        }
      }

      const archiveBlob = await this.archiveZip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: {
          level: 9
        },
        mimeType: 'application/zip'
      });

      const storageRef = this.afStorage.storage.refFromURL(storageTarget);
      await storageRef.put(archiveBlob, {
        contentType: 'application/zip'
      });
      const downloadUrl = await storageRef.getDownloadURL();

      this.archiveUrl = downloadUrl;
      this.modifiedFiles.clear();
      this.archiveStructureDirty = false;
      this.savedArchiveStructureSignature = this.captureArchiveStructureSignature();
      this.archiveSaveStatus = 'Archive saved just now.';

      if (this.selectedFile && this.canEditFile(this.selectedFile)) {
        this.selectedFile.originalText = this.selectedFile.rawText || '';
      }

      this.archiveSaved.emit({
        downloadUrl,
        sizeBytes: archiveBlob.size
      });
    } catch (error) {
      this.archiveSaveError = (error as Error).message || 'Failed to save archive changes.';
      this.archiveSaveStatus = null;
    } finally {
      this.archiveSaving = false;
      this.emitViewerStateChange();
      this.cdr.markForCheck();
    }
  }

  formatBytes(value: number | undefined): string {
    const size = typeof value === 'number' && Number.isFinite(value) ? value : 0;
    if (size < 1024) {
      return `${size} B`;
    }

    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  private async loadArchive(sourceKey: string | null = this.getArchiveSourceKey()): Promise<void> {
    this.loadVersion += 1;
    const loadVersion = this.loadVersion;
    const archiveUrl = String(this.archiveUrl || '').trim();
    this.pendingArchiveSourceKey = sourceKey;

    this.resetArchiveState(false);

    if (!archiveUrl) {
      this.loadedArchiveSourceKey = null;
      this.pendingArchiveSourceKey = null;
      this.loadedArchiveUrl = null;
      this.error = 'ZIP preview is not available for this artifact.';
      this.cdr.markForCheck();
      return;
    }

    if (!isPlatformBrowser(this.platformId)) {
      this.loadedArchiveSourceKey = null;
      this.pendingArchiveSourceKey = null;
      this.loadedArchiveUrl = null;
      this.error = 'ZIP preview is only available in the browser.';
      this.cdr.markForCheck();
      return;
    }

    this.loading = true;
    this.error = null;
    this.cdr.markForCheck();

    try {
      const response = await fetch(archiveUrl, {
        credentials: 'omit',
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error('Failed to download the ZIP artifact for preview.');
      }

      const zip = await JSZip.loadAsync(await response.arrayBuffer());
      if (loadVersion !== this.loadVersion) {
        return;
      }

      this.archiveZip = zip;
      this.explicitFolderPaths.clear();
      Object.values(zip.files)
        .filter(entry => entry.dir)
        .map(entry => this.normalizeArchivePath(entry.name))
        .filter(Boolean)
        .forEach(folderPath => this.explicitFolderPaths.add(folderPath));

      const entries = Object.values(zip.files)
        .filter(entry => !entry.dir)
        .map(entry => ({
          path: this.normalizeArchivePath(entry.name),
          entry
        }))
        .filter(entry => entry.path.length > 0);

      if (entries.length === 0) {
        throw new Error('This ZIP archive does not contain any files to preview.');
      }

      this.archiveEntries = new Map(entries.map(entry => [entry.path, entry]));
      this.fileCount = entries.length;
        this.treeNodes = this.buildTree(entries.map(entry => entry.path), Array.from(this.explicitFolderPaths));
        this.savedArchiveStructureSignature = this.captureArchiveStructureSignature();

      const initialFile = this.pickInitialFile(entries.map(entry => entry.path));
      if (initialFile) {
        await this.selectFile(initialFile, loadVersion, false);
      }

      this.loadedArchiveSourceKey = sourceKey;
      this.loadedArchiveUrl = archiveUrl;
    } catch (error) {
      if (loadVersion !== this.loadVersion) {
        return;
      }

      this.loadedArchiveSourceKey = null;
      this.loadedArchiveUrl = null;
      this.error = (error as Error).message || 'Failed to unpack the ZIP artifact.';
    } finally {
      if (loadVersion === this.loadVersion) {
        this.pendingArchiveSourceKey = null;
        this.loading = false;
        this.cdr.markForCheck();
      }
    }
  }

  private async selectFile(path: string, loadVersion: number = this.loadVersion, showLoadingState: boolean = true): Promise<void> {
    const normalizedPath = this.normalizeArchivePath(path);
    const descriptor = this.archiveEntries.get(normalizedPath);
    if (!descriptor) {
      this.selectedFileError = 'The selected file could not be found inside the archive.';
      this.cdr.markForCheck();
      return;
    }

    this.expandTreePath(normalizedPath);
    this.selectedFileLoading = showLoadingState;
    this.selectedFileError = null;
    if (showLoadingState) {
      this.cdr.markForCheck();
    }

    try {
      const metadata = this.getFileMetadata(normalizedPath);
      const originalText = this.isTextFileKind(metadata.kind)
        ? await this.getArchiveSourceText(normalizedPath)
        : null;
      const rawText = originalText === null
        ? null
        : (this.modifiedFiles.get(normalizedPath) ?? originalText);
      const bytes = rawText === null ? await descriptor.entry.async('uint8array') : undefined;
      if (loadVersion !== this.loadVersion) {
        return;
      }

      const limitedText = rawText === null ? null : this.limitTextPreview(rawText, MAX_TEXT_PREVIEW_BYTES);

      this.previewMode = this.canTogglePreviewMode({
        path: normalizedPath,
        name: normalizedPath.split('/').pop() || normalizedPath,
        kind: metadata.kind,
        language: metadata.language,
        mimeType: metadata.mimeType,
        sizeBytes: rawText === null ? bytes?.byteLength || 0 : this.getTextByteSize(limitedText?.text || ''),
        truncated: limitedText?.truncated === true
      }) ? 'preview' : 'code';
      this.editorOptions = this.buildEditorOptions(metadata.language, !this.isTextFileKind(metadata.kind));

      const nextSelectedFile: SelectedZipFile = {
        path: normalizedPath,
        name: normalizedPath.split('/').pop() || normalizedPath,
        kind: metadata.kind,
        language: metadata.language,
        mimeType: metadata.mimeType,
        sizeBytes: rawText === null ? bytes?.byteLength || 0 : this.getTextByteSize(rawText),
        ...(originalText !== null ? { originalText } : {}),
        truncated: limitedText?.truncated === true
      };

      if (rawText !== null) {
        nextSelectedFile.rawText = rawText;
      }

      await this.refreshSelectedFilePreview(nextSelectedFile);

      if (metadata.kind === 'image' || metadata.kind === 'audio' || metadata.kind === 'video') {
        nextSelectedFile.previewUrl = await this.getArchiveObjectUrl(normalizedPath, metadata.mimeType);
      }

      this.activeNodePath = normalizedPath;
      this.activeNodeType = 'file';
      this.selectedFolderPath = null;
      this.selectedFile = nextSelectedFile;
      this.emitViewerStateChange();
    } catch (error) {
      if (loadVersion !== this.loadVersion) {
        return;
      }

      this.selectedFileError = (error as Error).message || 'Failed to preview the selected file.';
    } finally {
      if (loadVersion === this.loadVersion) {
        this.selectedFileLoading = false;
        this.cdr.markForCheck();
      }
    }
  }

  private buildTree(paths: string[], explicitFolders: string[] = []): ZipTreeNode[] {
    const roots: ZipTreeNode[] = [];
    const nodeIndex = new Map<string, ZipTreeNode>();

    const ensureNode = (fullPath: string, finalType: ZipNodeType): void => {
      const segments = fullPath.split('/').filter(Boolean);
      let currentChildren = roots;
      let currentPath = '';

      segments.forEach((segment, index) => {
        currentPath = currentPath ? `${currentPath}/${segment}` : segment;
        const isLeaf = index === segments.length - 1;
        const nextType: ZipNodeType = isLeaf ? finalType : 'folder';
        let node = nodeIndex.get(currentPath);

        if (!node) {
          node = {
            name: segment,
            path: currentPath,
            type: nextType,
            depth: index,
            expanded: index < 2,
            children: []
          };
          currentChildren.push(node);
          nodeIndex.set(currentPath, node);
        } else if (nextType === 'folder') {
          node.type = 'folder';
        }

        currentChildren = node.children;
      });
    };

    for (const folderPath of [...explicitFolders].sort((left, right) => left.localeCompare(right))) {
      ensureNode(folderPath, 'folder');
    }

    for (const filePath of [...paths].sort((left, right) => left.localeCompare(right))) {
      ensureNode(filePath, 'file');
    }

    const sortNodes = (nodes: ZipTreeNode[]): ZipTreeNode[] => nodes
      .sort((left, right) => {
        if (left.type !== right.type) {
          return left.type === 'folder' ? -1 : 1;
        }

        return left.name.localeCompare(right.name);
      })
      .map(node => ({
        ...node,
        children: sortNodes(node.children)
      }));

    return sortNodes(roots);
  }

  private pickInitialFile(paths: string[]): string | null {
    const normalizedPaths = paths.map(path => this.normalizeArchivePath(path));

    for (const candidate of FILE_PICK_PRIORITY) {
      if (normalizedPaths.includes(candidate)) {
        return candidate;
      }
    }

    const preferredHtml = normalizedPaths.find(path => path.endsWith('.html'));
    if (preferredHtml) {
      return preferredHtml;
    }

    const preferredCode = normalizedPaths.find(path => this.getFileMetadata(path).kind === 'code');
    if (preferredCode) {
      return preferredCode;
    }

    return normalizedPaths[0] || null;
  }

  private expandTreePath(path: string): void {
    const prefixes = path.split('/').filter(Boolean).map((_segment, index, segments) => segments.slice(0, index + 1).join('/'));
    const markExpanded = (nodes: ZipTreeNode[]): void => {
      nodes.forEach(node => {
        if (node.type === 'folder' && prefixes.includes(node.path)) {
          node.expanded = true;
        }
        if (node.children.length > 0) {
          markExpanded(node.children);
        }
      });
    };

    markExpanded(this.treeNodes);
  }

  private getFileMetadata(path: string): { kind: ZipFileKind; mimeType: string; language: string } {
    const normalizedPath = path.toLowerCase();
    const extension = normalizedPath.includes('.') ? normalizedPath.slice(normalizedPath.lastIndexOf('.')) : '';

    switch (extension) {
      case '.html':
      case '.htm':
        return { kind: 'html', mimeType: 'text/html', language: 'html' };
      case '.svg':
        return { kind: 'svg', mimeType: 'image/svg+xml', language: 'xml' };
      case '.png':
        return { kind: 'image', mimeType: 'image/png', language: 'plaintext' };
      case '.jpg':
      case '.jpeg':
        return { kind: 'image', mimeType: 'image/jpeg', language: 'plaintext' };
      case '.gif':
        return { kind: 'image', mimeType: 'image/gif', language: 'plaintext' };
      case '.webp':
        return { kind: 'image', mimeType: 'image/webp', language: 'plaintext' };
      case '.mp3':
        return { kind: 'audio', mimeType: 'audio/mpeg', language: 'plaintext' };
      case '.wav':
        return { kind: 'audio', mimeType: 'audio/wav', language: 'plaintext' };
      case '.ogg':
        return { kind: 'audio', mimeType: 'audio/ogg', language: 'plaintext' };
      case '.mp4':
        return { kind: 'video', mimeType: 'video/mp4', language: 'plaintext' };
      case '.webm':
        return { kind: 'video', mimeType: 'video/webm', language: 'plaintext' };
      case '.css':
        return { kind: 'code', mimeType: 'text/css', language: 'css' };
      case '.csv':
        return { kind: 'csv', mimeType: 'text/csv', language: 'plaintext' };
      case '.scss':
        return { kind: 'code', mimeType: 'text/x-scss', language: 'scss' };
      case '.js':
      case '.mjs':
      case '.cjs':
        return { kind: 'code', mimeType: 'text/javascript', language: 'javascript' };
      case '.ts':
        return { kind: 'code', mimeType: 'text/typescript', language: 'typescript' };
      case '.tsx':
        return { kind: 'code', mimeType: 'text/typescript', language: 'typescript' };
      case '.jsx':
        return { kind: 'code', mimeType: 'text/javascript', language: 'javascript' };
      case '.json':
        return { kind: 'code', mimeType: 'application/json', language: 'json' };
      case '.markdown':
      case '.md':
        return { kind: 'markdown', mimeType: 'text/markdown', language: 'markdown' };
      case '.txt':
        return { kind: 'code', mimeType: 'text/plain', language: 'plaintext' };
      case '.xml':
        return { kind: 'code', mimeType: 'application/xml', language: 'xml' };
      case '.yml':
      case '.yaml':
        return { kind: 'code', mimeType: 'application/yaml', language: 'yaml' };
      case '.sh':
        return { kind: 'code', mimeType: 'text/x-shellscript', language: 'shell' };
      case '.env':
        return { kind: 'code', mimeType: 'text/plain', language: 'shell' };
      default:
        if (!extension) {
          return { kind: 'code', mimeType: 'text/plain', language: 'plaintext' };
        }
        return { kind: 'binary', mimeType: 'application/octet-stream', language: 'plaintext' };
    }
  }

  private buildEditorOptions(language: string, readOnly: boolean): Record<string, unknown> {
    return {
      theme: this.theme.isLight ? 'vs' : 'vs-dark',
      language,
      readOnly,
      automaticLayout: true,
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      wordWrap: 'on',
      fontSize: 12,
      lineNumbersMinChars: 3
    };
  }

  private isTextFileKind(kind: ZipFileKind): boolean {
    return kind === 'code' || kind === 'markdown' || kind === 'csv' || kind === 'html' || kind === 'svg';
  }

  private async refreshSelectedFilePreview(file: SelectedZipFile): Promise<void> {
    if (file.kind === 'html') {
      file.previewText = await this.buildHtmlPreview(file.path, file.rawText || '');
    } else if (file.kind === 'svg') {
      file.previewText = file.rawText || '';
    }

    if (this.selectedFile?.path === file.path) {
      this.selectedFile = {
        ...file
      };
    }

    this.cdr.markForCheck();
  }

  private emitViewerStateChange(): void {
    this.viewerStateChange.emit({
      canTogglePreviewMode: this.canTogglePreviewMode(this.selectedFile),
      canSaveArchive: this.canSaveArchive(),
      archiveSaving: this.archiveSaving,
      previewMode: this.selectedFile ? this.previewMode : null,
      explorerVisibility: this.explorerVisibility
    });
  }

  private getClampedExplorerWidth(nextWidth: number): number {
    return Math.max(this.minExplorerWidth, Math.min(this.maxExplorerWidth, Math.round(nextWidth)));
  }

  private stopExplorerResize(): void {
    this.releaseExplorerPointerCapture();

    if (!this.isExplorerResizing) {
      return;
    }

    this.isExplorerResizing = false;
    this.setResizeCursorState(false);
    this.cdr.markForCheck();
  }

  private releaseExplorerPointerCapture(): void {
    if (this.explorerResizeHandle && this.explorerResizePointerId !== null) {
      try {
        if (this.explorerResizeHandle.hasPointerCapture?.(this.explorerResizePointerId)) {
          this.explorerResizeHandle.releasePointerCapture?.(this.explorerResizePointerId);
        }
      } catch {
        // Ignore stale capture state when the divider is removed mid-interaction.
      }
    }

    this.explorerResizeHandle = null;
    this.explorerResizePointerId = null;
  }

  private setResizeCursorState(isActive: boolean): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.body.style.cursor = isActive ? 'col-resize' : '';
    document.body.style.userSelect = isActive ? 'none' : '';
  }

  private async getArchiveText(path: string): Promise<string> {
    const modifiedValue = this.modifiedFiles.get(path);
    if (typeof modifiedValue === 'string') {
      return modifiedValue;
    }

    return this.getArchiveSourceText(path);
  }

  private async getArchiveSourceText(path: string): Promise<string> {

    const descriptor = this.archiveEntries.get(path);
    if (!descriptor) {
      throw new Error('Archive text could not be resolved.');
    }

    return descriptor.entry.async('string');
  }

  private limitTextPreview(value: string, maxBytes: number): { text: string; truncated: boolean } {
    if (this.getTextByteSize(value) <= maxBytes) {
      return { text: value, truncated: false };
    }

    return {
      text: this.truncateTextToBytes(value, maxBytes),
      truncated: true
    };
  }

  private getTextByteSize(value: string): number {
    return new TextEncoder().encode(value).length;
  }

  private truncateTextToBytes(value: string, maxBytes: number): string {
    if (maxBytes <= 0) {
      return '';
    }

    const encoded = new TextEncoder().encode(value);
    if (encoded.length <= maxBytes) {
      return value;
    }

    return new TextDecoder('utf-8').decode(encoded.slice(0, maxBytes));
  }

  private async buildHtmlPreview(path: string, html: string): Promise<string> {
    const parser = new DOMParser();
    const document = parser.parseFromString(html || '<!doctype html><html><head></head><body></body></html>', 'text/html');
    const urlAttributes = ['src', 'href', 'poster'];

    for (const attributeName of urlAttributes) {
      const elements = Array.from(document.querySelectorAll<HTMLElement>(`[${attributeName}]`));
      for (const element of elements) {
        const nextValue = await this.rewriteReferenceValue(path, element.getAttribute(attributeName));
        if (nextValue) {
          element.setAttribute(attributeName, nextValue);
        }
      }
    }

    const srcsetElements = Array.from(document.querySelectorAll<HTMLElement>('[srcset]'));
    for (const element of srcsetElements) {
      const rewrittenSrcset = await this.rewriteSrcsetValue(path, element.getAttribute('srcset'));
      if (rewrittenSrcset) {
        element.setAttribute('srcset', rewrittenSrcset);
      }
    }

    const styleElements = Array.from(document.querySelectorAll('style'));
    for (const styleElement of styleElements) {
      styleElement.textContent = await this.rewriteCssText(styleElement.textContent || '', path);
    }

    return `<!DOCTYPE html>\n${document.documentElement.outerHTML}`;
  }

  private async rewriteSrcsetValue(currentFilePath: string, srcset: string | null): Promise<string | null> {
    const normalizedSrcset = String(srcset || '').trim();
    if (!normalizedSrcset) {
      return null;
    }

    const parts = normalizedSrcset.split(',').map(part => part.trim()).filter(Boolean);
    const rewrittenParts = await Promise.all(parts.map(async part => {
      const [urlPart, ...descriptorParts] = part.split(/\s+/);
      const rewrittenUrl = await this.rewriteReferenceValue(currentFilePath, urlPart);
      return [rewrittenUrl || urlPart, ...descriptorParts].join(' ').trim();
    }));

    return rewrittenParts.join(', ');
  }

  private async rewriteCssText(css: string, currentFilePath: string): Promise<string> {
    let output = css;

    const importMatches = Array.from(css.matchAll(/@import\s+(url\(\s*)?(['"]?)([^'"\s)]+)\2\s*\)?([^;]*);/g));
    for (const match of importMatches) {
      const original = match[0];
      const importPath = match[3] || '';
      const trailingClause = match[4] || '';
      const rewritten = await this.rewriteReferenceValue(currentFilePath, importPath);
      if (rewritten) {
        output = output.replace(original, `@import url("${rewritten}")${trailingClause};`);
      }
    }

    const matches = Array.from(output.matchAll(/url\(([^)]+)\)/g));
    if (matches.length === 0) {
      return output;
    }

    for (const match of matches) {
      const original = match[0];
      const rawValue = match[1]?.trim().replace(/^['"]|['"]$/g, '') || '';
      const rewritten = await this.rewriteReferenceValue(currentFilePath, rawValue);
      if (rewritten) {
        output = output.replace(original, `url("${rewritten}")`);
      }
    }

    return output;
  }

  private async rewriteReferenceValue(currentFilePath: string, value: string | null): Promise<string | null> {
    const rawValue = String(value || '').trim();
    if (!rawValue || this.isExternalReference(rawValue)) {
      return rawValue || null;
    }

    const resolvedPath = this.resolveArchivePath(currentFilePath, rawValue);
    if (!resolvedPath || !this.archiveEntries.has(resolvedPath)) {
      return rawValue;
    }

    const mimeType = this.getFileMetadata(resolvedPath).mimeType;
    return this.getArchiveObjectUrl(resolvedPath, mimeType);
  }

  private isExternalReference(value: string): boolean {
    return value.startsWith('#') || /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(value);
  }

  private resolveArchivePath(currentFilePath: string, targetPath: string): string | null {
    const strippedPath = String(targetPath || '').split('#')[0]?.split('?')[0] || '';
    const decodedPath = this.safeDecodeUriComponent(strippedPath);
    if (!decodedPath) {
      return null;
    }

    if (decodedPath.startsWith('/')) {
      return this.normalizeArchivePath(decodedPath);
    }

    const currentDirectory = currentFilePath.includes('/')
      ? currentFilePath.slice(0, currentFilePath.lastIndexOf('/'))
      : '';

    return this.normalizeArchivePath(currentDirectory ? `${currentDirectory}/${decodedPath}` : decodedPath);
  }

  private safeDecodeUriComponent(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  private normalizeArchivePath(value: string): string {
    const segments = String(value || '')
      .replace(/\\/g, '/')
      .split('/')
      .filter(segment => segment.length > 0);
    const normalizedSegments: string[] = [];

    for (const segment of segments) {
      if (segment === '.') {
        continue;
      }

      if (segment === '..') {
        normalizedSegments.pop();
        continue;
      }

      normalizedSegments.push(segment);
    }

    return normalizedSegments.join('/');
  }

  private async getArchiveObjectUrl(path: string, mimeType: string): Promise<string> {
    const cacheKey = `${path}:${mimeType}`;
    const existingUrl = this.objectUrlCache.get(cacheKey);
    if (existingUrl) {
      return existingUrl;
    }

    const descriptor = this.archiveEntries.get(path);
    if (!descriptor) {
      throw new Error('Archive asset could not be resolved.');
    }

    const metadata = this.getFileMetadata(path);
    let blob: Blob;

    if (this.isTextFileKind(metadata.kind)) {
      const text = await this.getArchiveText(path);
      const content = mimeType === 'text/css'
        ? await this.rewriteCssText(text, path)
        : text;
      blob = new Blob([content], { type: mimeType });
    } else {
      const bytes = await descriptor.entry.async('uint8array');
      blob = new Blob([bytes], { type: mimeType });
    }

    const url = URL.createObjectURL(blob);
    this.createdObjectUrls.add(url);
    this.objectUrlCache.set(cacheKey, url);
    return url;
  }

  private invalidateObjectUrlCacheForPath(path: string): void {
    const cacheKeys = Array.from(this.objectUrlCache.keys()).filter(cacheKey => cacheKey.startsWith(`${path}:`));
    for (const cacheKey of cacheKeys) {
      const existingUrl = this.objectUrlCache.get(cacheKey);
      if (existingUrl) {
        URL.revokeObjectURL(existingUrl);
        this.createdObjectUrls.delete(existingUrl);
      }
      this.objectUrlCache.delete(cacheKey);
    }
  }

  private openExplorerAction(type: ZipExplorerActionType, value: string): void {
    this.explorerAction = {
      type,
      value,
      error: null
    };
    this.cdr.markForCheck();
  }

  private buildSuggestedPath(type: ZipExplorerActionType): string {
    const directory = this.getPreferredDirectoryPath();
    const baseName = type === 'create-folder' ? 'new-folder' : 'untitled.txt';
    return directory ? `${directory}/${baseName}` : baseName;
  }

  private getPreferredDirectoryPath(): string {
    if (this.activeNodeType === 'folder' && this.activeNodePath) {
      return this.activeNodePath;
    }

    if (this.activeNodeType === 'file' && this.activeNodePath) {
      return this.getParentDirectory(this.activeNodePath);
    }

    return '';
  }

  private getParentDirectory(pathValue: string): string {
    const normalized = this.normalizeArchivePath(pathValue);
    if (!normalized || !normalized.includes('/')) {
      return '';
    }

    return normalized.slice(0, normalized.lastIndexOf('/'));
  }

  private pathExists(pathValue: string): boolean {
    return this.archiveEntries.has(pathValue) || this.folderExists(pathValue);
  }

  private folderExists(pathValue: string): boolean {
    if (!pathValue) {
      return false;
    }

    if (this.explicitFolderPaths.has(pathValue)) {
      return true;
    }

    const prefix = `${pathValue}/`;
    return Array.from(this.archiveEntries.keys()).some(entryPath => entryPath.startsWith(prefix))
      || Array.from(this.explicitFolderPaths).some(folderPath => folderPath.startsWith(prefix));
  }

  private hasFileAncestor(pathValue: string): boolean {
    const segments = this.normalizeArchivePath(pathValue).split('/').filter(Boolean);
    let currentPath = '';

    for (let index = 0; index < segments.length - 1; index += 1) {
      currentPath = currentPath ? `${currentPath}/${segments[index]}` : segments[index];
      if (this.archiveEntries.has(currentPath)) {
        return true;
      }
    }

    return false;
  }

  private rebuildTree(activePath: string | null = this.activeNodePath, activeType: ZipNodeType | null = this.activeNodeType): void {
    const filePaths = Array.from(this.archiveEntries.keys()).sort((left, right) => left.localeCompare(right));
    const folderPaths = Array.from(this.explicitFolderPaths).sort((left, right) => left.localeCompare(right));
    this.fileCount = filePaths.length;
    this.treeNodes = this.buildTree(filePaths, folderPaths);

    if (activePath) {
      this.expandTreePath(activePath);
    }

    this.activeNodePath = activePath;
    this.activeNodeType = activeType;
  }

  private captureArchiveStructureSignature(): string {
    const filePaths = Array.from(this.archiveEntries.keys()).sort((left, right) => left.localeCompare(right));
    const folderPaths = Array.from(this.explicitFolderPaths).sort((left, right) => left.localeCompare(right));
    return JSON.stringify({
      files: filePaths,
      folders: folderPaths
    });
  }

  private buildUniquePath(parentDirectory: string, baseName: string, nodeType: ZipNodeType): string {
    const normalizedParent = this.normalizeArchivePath(parentDirectory);
    const normalizedBaseName = String(baseName || '').trim();
    const extensionIndex = nodeType === 'file' ? normalizedBaseName.lastIndexOf('.') : -1;
    const stem = extensionIndex > 0 ? normalizedBaseName.slice(0, extensionIndex) : normalizedBaseName;
    const extension = extensionIndex > 0 ? normalizedBaseName.slice(extensionIndex) : '';

    let attempt = 0;
    while (attempt < 500) {
      const candidateName = attempt === 0
        ? normalizedBaseName
        : `${stem}-${attempt + 1}${extension}`;
      const candidatePath = this.normalizeArchivePath(normalizedParent ? `${normalizedParent}/${candidateName}` : candidateName);
      if (!this.pathExists(candidatePath)) {
        return candidatePath;
      }
      attempt += 1;
    }

    return this.normalizeArchivePath(normalizedParent ? `${normalizedParent}/${normalizedBaseName}-${Date.now()}` : `${normalizedBaseName}-${Date.now()}`);
  }

  private buildDuplicatePath(sourcePath: string, nodeType: ZipNodeType): string {
    const parentDirectory = this.getParentDirectory(sourcePath);
    const baseName = this.getPathBasename(sourcePath);
    const extensionIndex = nodeType === 'file' ? baseName.lastIndexOf('.') : -1;
    const stem = extensionIndex > 0 ? baseName.slice(0, extensionIndex) : baseName;
    const extension = extensionIndex > 0 ? baseName.slice(extensionIndex) : '';
    const duplicateBaseName = `${stem}-copy${extension}`;
    return this.buildUniquePath(parentDirectory, duplicateBaseName, nodeType);
  }

  private buildMovedPath(sourcePath: string, targetDirectory: string): string {
    const basename = this.getPathBasename(sourcePath);
    return this.normalizeArchivePath(targetDirectory ? `${targetDirectory}/${basename}` : basename);
  }

  private getDropDirectoryForNode(node: ZipTreeNode): string {
    return node.type === 'folder' ? node.path : this.getParentDirectory(node.path);
  }

  private isDropLocationUnchanged(targetDirectory: string): boolean {
    if (!this.draggedNodePath) {
      return false;
    }

    return this.buildMovedPath(this.draggedNodePath, targetDirectory) === this.draggedNodePath;
  }

  private canHoverDirectoryTarget(targetDirectory: string): boolean {
    if (!this.draggedNodePath || !this.draggedNodeType) {
      return false;
    }

    const normalizedTarget = this.normalizeArchivePath(targetDirectory);
    if (this.draggedNodeType === 'folder') {
      if (normalizedTarget === this.draggedNodePath || normalizedTarget.startsWith(`${this.draggedNodePath}/`)) {
        return false;
      }
    }

    const nextPath = this.buildMovedPath(this.draggedNodePath, normalizedTarget);
    if (!nextPath) {
      return false;
    }

    if (nextPath !== this.draggedNodePath && this.pathExists(nextPath)) {
      return false;
    }

    return !this.hasFileAncestor(nextPath);
  }

  private canDropIntoDirectory(targetDirectory: string): boolean {
    if (!this.canHoverDirectoryTarget(targetDirectory) || !this.draggedNodePath) {
      return false;
    }

    return !!this.buildMovedPath(this.draggedNodePath, targetDirectory);
  }

  private async moveDraggedNodeToDirectory(targetDirectory: string): Promise<void> {
    const draggedPath = this.draggedNodePath;
    const draggedType = this.draggedNodeType;
    const normalizedTarget = this.normalizeArchivePath(targetDirectory);

    if (!draggedPath || !draggedType || !this.canDropIntoDirectory(normalizedTarget)) {
      this.onTreeDragEnd();
      return;
    }

    const nextPath = this.buildMovedPath(draggedPath, normalizedTarget);
    if (!nextPath || nextPath === draggedPath) {
      this.onTreeDragEnd();
      return;
    }

    try {
      if (draggedType === 'file') {
        await this.moveFileEntry(draggedPath, nextPath);
        this.archiveStructureDirty = true;
        this.archiveSaveError = null;
        this.archiveSaveStatus = null;
        this.rebuildTree(nextPath, 'file');
        await this.selectFile(nextPath);
      } else {
        await this.moveFolderEntry(draggedPath, nextPath);
        this.archiveStructureDirty = true;
        this.archiveSaveError = null;
        this.archiveSaveStatus = null;
        this.selectedFile = null;
        this.selectedFolderPath = nextPath;
        this.selectedFileLoading = false;
        this.selectedFileError = null;
        this.previewMode = 'preview';
        this.rebuildTree(nextPath, 'folder');
      }
    } catch (error) {
      this.archiveSaveError = (error as Error).message || 'Unable to move this archive item.';
      this.archiveSaveStatus = null;
    } finally {
      this.onTreeDragEnd();
    }
  }

  private async createArchiveFile(pathValue: string): Promise<void> {
    if (!this.archiveZip) {
      throw new Error('Archive is not ready yet.');
    }

    const normalizedPath = this.normalizeArchivePath(pathValue);
    if (!normalizedPath) {
      throw new Error('Choose a file path inside the archive.');
    }

    if (this.pathExists(normalizedPath)) {
      throw new Error('A file or folder already exists at that path.');
    }

    if (this.hasFileAncestor(normalizedPath)) {
      throw new Error('One of the parent paths is already a file.');
    }

    this.archiveZip.file(normalizedPath, '');
    const entry = this.archiveZip.file(normalizedPath);
    if (!entry) {
      throw new Error('Unable to create the new file in the archive.');
    }

    this.archiveEntries.set(normalizedPath, { path: normalizedPath, entry });
    this.modifiedFiles.set(normalizedPath, '');
    this.archiveStructureDirty = true;
    this.archiveSaveError = null;
    this.archiveSaveStatus = null;
    this.rebuildTree(normalizedPath, 'file');
    await this.selectFile(normalizedPath);
  }

  private async duplicateArchiveFile(sourcePath: string): Promise<void> {
    if (!this.archiveZip) {
      throw new Error('Archive is not ready yet.');
    }

    const normalizedSourcePath = this.normalizeArchivePath(sourcePath);
    const descriptor = this.archiveEntries.get(normalizedSourcePath);
    if (!descriptor) {
      throw new Error('The selected archive file no longer exists.');
    }

    const duplicatePath = this.buildDuplicatePath(normalizedSourcePath, 'file');
    const modifiedText = this.modifiedFiles.get(normalizedSourcePath);

    if (typeof modifiedText === 'string') {
      this.archiveZip.file(duplicatePath, modifiedText);
    } else if (this.isTextFileKind(this.getFileMetadata(normalizedSourcePath).kind)) {
      this.archiveZip.file(duplicatePath, await descriptor.entry.async('string'));
    } else {
      this.archiveZip.file(duplicatePath, await descriptor.entry.async('uint8array'));
    }

    const entry = this.archiveZip.file(duplicatePath);
    if (!entry) {
      throw new Error('Unable to duplicate this archive file.');
    }

    this.archiveEntries.set(duplicatePath, { path: duplicatePath, entry });
    this.archiveStructureDirty = true;
    this.archiveSaveError = null;
    this.archiveSaveStatus = null;
    this.rebuildTree(duplicatePath, 'file');
    await this.selectFile(duplicatePath);
  }

  private createArchiveFolder(pathValue: string): void {
    if (!this.archiveZip) {
      throw new Error('Archive is not ready yet.');
    }

    const normalizedPath = this.normalizeArchivePath(pathValue);
    if (!normalizedPath) {
      throw new Error('Choose a folder path inside the archive.');
    }

    if (this.pathExists(normalizedPath)) {
      throw new Error('A file or folder already exists at that path.');
    }

    if (this.hasFileAncestor(normalizedPath)) {
      throw new Error('One of the parent paths is already a file.');
    }

    this.archiveZip.folder(normalizedPath);
    this.explicitFolderPaths.add(normalizedPath);
    this.archiveStructureDirty = true;
    this.archiveSaveError = null;
    this.archiveSaveStatus = null;
    this.selectedFile = null;
    this.selectedFolderPath = normalizedPath;
    this.selectedFileError = null;
    this.selectedFileLoading = false;
    this.previewMode = 'preview';
    this.rebuildTree(normalizedPath, 'folder');
    this.cdr.markForCheck();
  }

  private async renameOrMoveNode(pathValue: string): Promise<void> {
    if (!this.archiveZip || !this.activeNodePath || !this.activeNodeType) {
      throw new Error('Select a file or folder first.');
    }

    const normalizedPath = this.normalizeArchivePath(pathValue);
    if (!normalizedPath) {
      throw new Error('Choose a new archive path.');
    }

    if (normalizedPath === this.activeNodePath) {
      return;
    }

    if (this.hasFileAncestor(normalizedPath)) {
      throw new Error('One of the parent paths is already a file.');
    }

    if (this.activeNodeType === 'file') {
      if (this.pathExists(normalizedPath)) {
        throw new Error('A file or folder already exists at that destination.');
      }

      await this.moveFileEntry(this.activeNodePath, normalizedPath);
      this.archiveStructureDirty = true;
      this.archiveSaveError = null;
      this.archiveSaveStatus = null;
      this.rebuildTree(normalizedPath, 'file');
      await this.selectFile(normalizedPath);
      return;
    }

    if (normalizedPath.startsWith(`${this.activeNodePath}/`)) {
      throw new Error('A folder cannot be moved inside itself.');
    }

    if (this.pathExists(normalizedPath)) {
      throw new Error('A file or folder already exists at that destination.');
    }

    await this.moveFolderEntry(this.activeNodePath, normalizedPath);
    this.archiveStructureDirty = true;
    this.archiveSaveError = null;
    this.archiveSaveStatus = null;
    this.selectedFile = null;
    this.selectedFolderPath = normalizedPath;
    this.selectedFileError = null;
    this.selectedFileLoading = false;
    this.rebuildTree(normalizedPath, 'folder');
    this.cdr.markForCheck();
  }

  private async moveFolderEntry(oldPath: string, newPath: string): Promise<void> {
    const oldPrefix = `${oldPath}/`;
    const folderPaths = Array.from(this.explicitFolderPaths)
      .filter(folderPath => folderPath === oldPath || folderPath.startsWith(oldPrefix))
      .sort((left, right) => left.localeCompare(right));
    const filePaths = Array.from(this.archiveEntries.keys())
      .filter(filePath => filePath.startsWith(oldPrefix))
      .sort((left, right) => left.localeCompare(right));

    const renamedFileTargets = filePaths.map(filePath => ({
      from: filePath,
      to: `${newPath}${filePath.slice(oldPath.length)}`
    }));
    const renamedFolderTargets = folderPaths.map(folderPath => ({
      from: folderPath,
      to: `${newPath}${folderPath.slice(oldPath.length)}`
    }));

    for (const candidate of renamedFileTargets) {
      if ((this.archiveEntries.has(candidate.to) || this.folderExists(candidate.to)) && !candidate.to.startsWith(oldPrefix)) {
        throw new Error(`Cannot move folder because ${candidate.to} already exists.`);
      }
      if (this.hasFileAncestor(candidate.to)) {
        throw new Error(`Cannot move folder because ${candidate.to} has a file in its parent path.`);
      }
    }

    for (const candidate of renamedFolderTargets) {
      if (candidate.to !== newPath && this.folderExists(candidate.to) && !candidate.to.startsWith(oldPrefix)) {
        throw new Error(`Cannot move folder because ${candidate.to} already exists.`);
      }
    }

    for (const candidate of renamedFileTargets) {
      await this.moveFileEntry(candidate.from, candidate.to);
    }

    folderPaths
      .sort((left, right) => right.length - left.length)
      .forEach(folderPath => {
        this.archiveZip?.remove(`${folderPath}/`);
        this.explicitFolderPaths.delete(folderPath);
      });

    if (folderPaths.length === 0) {
      this.archiveZip?.remove(`${oldPath}/`);
    }

    renamedFolderTargets.forEach(candidate => {
      this.archiveZip?.folder(candidate.to);
      this.explicitFolderPaths.add(candidate.to);
    });

    if (renamedFolderTargets.length === 0) {
      this.archiveZip?.folder(newPath);
      this.explicitFolderPaths.add(newPath);
    }
  }

  private async moveFileEntry(oldPath: string, newPath: string): Promise<void> {
    if (!this.archiveZip) {
      throw new Error('Archive is not ready yet.');
    }

    const descriptor = this.archiveEntries.get(oldPath);
    if (!descriptor) {
      throw new Error('The selected archive file no longer exists.');
    }

    const modifiedText = this.modifiedFiles.get(oldPath);
    if (typeof modifiedText === 'string') {
      this.archiveZip.file(newPath, modifiedText);
      this.modifiedFiles.delete(oldPath);
      this.modifiedFiles.set(newPath, modifiedText);
    } else if (this.isTextFileKind(this.getFileMetadata(oldPath).kind)) {
      this.archiveZip.file(newPath, await descriptor.entry.async('string'));
    } else {
      this.archiveZip.file(newPath, await descriptor.entry.async('uint8array'));
    }

    this.archiveZip.remove(oldPath);
    const nextEntry = this.archiveZip.file(newPath);
    if (!nextEntry) {
      throw new Error('Unable to update the archive file path.');
    }

    this.archiveEntries.delete(oldPath);
    this.archiveEntries.set(newPath, { path: newPath, entry: nextEntry });
    this.invalidateObjectUrlCacheForPath(oldPath);
    this.invalidateObjectUrlCacheForPath(newPath);
  }

  private async deleteArchiveNode(pathValue: string, nodeType: ZipNodeType): Promise<void> {
    if (!this.archiveZip) {
      throw new Error('Archive is not ready yet.');
    }

    const normalizedPath = this.normalizeArchivePath(pathValue);
    const normalizedPrefix = `${normalizedPath}/`;

    if (nodeType === 'file') {
      this.archiveZip.remove(normalizedPath);
      this.archiveEntries.delete(normalizedPath);
      this.modifiedFiles.delete(normalizedPath);
      this.invalidateObjectUrlCacheForPath(normalizedPath);
    } else {
      Array.from(this.archiveEntries.keys())
        .filter(entryPath => entryPath.startsWith(normalizedPrefix))
        .forEach(entryPath => {
          this.archiveZip?.remove(entryPath);
          this.archiveEntries.delete(entryPath);
          this.modifiedFiles.delete(entryPath);
          this.invalidateObjectUrlCacheForPath(entryPath);
        });

      Array.from(this.explicitFolderPaths)
        .filter(folderPath => folderPath === normalizedPath || folderPath.startsWith(normalizedPrefix))
        .sort((left, right) => right.length - left.length)
        .forEach(folderPath => {
          this.archiveZip?.remove(`${folderPath}/`);
          this.explicitFolderPaths.delete(folderPath);
        });

      this.archiveZip.remove(`${normalizedPath}/`);
    }

    this.archiveStructureDirty = true;
    this.archiveSaveError = null;
    this.archiveSaveStatus = null;
    this.resetAddPopoverState();
    this.closeRenamePopover();

    const parentDirectory = this.getParentDirectory(normalizedPath);
    if (parentDirectory && this.folderExists(parentDirectory)) {
      this.selectedFile = null;
      this.selectedFolderPath = parentDirectory;
      this.selectedFileLoading = false;
      this.selectedFileError = null;
      this.previewMode = 'preview';
      this.rebuildTree(parentDirectory, 'folder');
    } else {
      this.selectedFile = null;
      this.selectedFolderPath = null;
      this.selectedFileLoading = false;
      this.selectedFileError = null;
      this.previewMode = 'preview';
      this.rebuildTree(null, null);
    }

    this.emitViewerStateChange();

    this.cdr.markForCheck();
  }

  private resetArchiveState(markForCheck: boolean = true): void {
    this.loading = false;
    this.error = null;
    this.fileCount = 0;
    this.treeNodes = [];
    this.selectedFile = null;
    this.selectedFolderPath = null;
    this.selectedFileLoading = false;
    this.selectedFileError = null;
    this.previewMode = 'preview';
    this.archiveSaving = false;
    this.archiveDeleting = false;
    this.archiveSaveError = null;
    this.archiveSaveStatus = null;
    this.activeNodePath = null;
    this.activeNodeType = null;
    this.activePopoverKind = null;
    this.renameTargetNode = null;
    this.deleteTargetNode = null;
    this.renameDraft = '';
    this.renameError = null;
    this.draggedNodePath = null;
    this.draggedNodeType = null;
    this.dropDirectoryPath = null;
    this.dropHoverNodePath = null;
    this.explorerAction = null;
    this.archiveZip = null;
    this.archiveEntries.clear();
    this.explicitFolderPaths.clear();
    this.modifiedFiles.clear();
    this.objectUrlCache.clear();
    this.archiveStructureDirty = false;
    this.savedArchiveStructureSignature = '';

    this.createdObjectUrls.forEach(url => URL.revokeObjectURL(url));
    this.createdObjectUrls.clear();

    this.emitViewerStateChange();

    if (markForCheck) {
      this.cdr.markForCheck();
    }
  }

  private getArchiveSourceKey(): string | null {
    const storageUri = String(this.archiveStorageUri || '').trim();
    if (storageUri) {
      return storageUri;
    }

    const archiveUrl = String(this.archiveUrl || '').trim();
    return archiveUrl || null;
  }

  private closeAnyPopover(): void {
    if (!this.activePopoverKind) {
      return;
    }

    this.popoverService.close();
  }

  private resetAddPopoverState(): void {
    if (this.activePopoverKind === 'add') {
      this.activePopoverKind = null;
    }
  }

  private resetArchiveDeletePopoverState(): void {
    if (this.activePopoverKind === 'archive-delete') {
      this.activePopoverKind = null;
    }
  }

  private resetRenamePopoverState(): void {
    if (this.activePopoverKind === 'rename') {
      this.activePopoverKind = null;
    }

    this.renameTargetNode = null;
    this.renameDraft = '';
    this.renameError = null;
  }

  private resetDeletePopoverState(): void {
    if (this.activePopoverKind === 'delete') {
      this.activePopoverKind = null;
    }

    this.deleteTargetNode = null;
  }

  private focusRenamePopoverInput(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    window.setTimeout(() => {
      const input = this.renamePopoverInput?.nativeElement
        || document.querySelector('.zip-rename-popover__input') as HTMLInputElement | null;
      input?.focus({ preventScroll: true });
      input?.select();
    }, 30);
  }

  private getStorageReferenceTarget(): string | null {
    const storageUri = String(this.archiveStorageUri || '').trim();
    if (/^gs:\/\//i.test(storageUri)) {
      return storageUri;
    }

    const archiveUrl = String(this.archiveUrl || '').trim();
    if (/^https:\/\/firebasestorage\.googleapis\.com\//i.test(archiveUrl)) {
      return archiveUrl;
    }

    return null;
  }

  private parseCsvRows(csv: string): string[][] {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentCell = '';
    let inQuotes = false;

    for (let index = 0; index < csv.length; index += 1) {
      const char = csv[index];
      const nextChar = csv[index + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          currentCell += '"';
          index += 1;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }

      if (char === ',' && !inQuotes) {
        currentRow.push(currentCell);
        currentCell = '';
        continue;
      }

      if ((char === '\n' || char === '\r') && !inQuotes) {
        if (char === '\r' && nextChar === '\n') {
          index += 1;
        }

        currentRow.push(currentCell);
        if (currentRow.some(value => value.length > 0)) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentCell = '';
        continue;
      }

      currentCell += char;
    }

    currentRow.push(currentCell);
    if (currentRow.some(value => value.length > 0)) {
      rows.push(currentRow);
    }

    return rows;
  }
}
